// packages/console/src/api/whep.ts
//
// WHEP client for the AV1 low-latency egress. A minted playback token (the SAME
// token as HLS) authorises WHEP; the token rides the path. This does the standard
// WHEP handshake (recvonly AV1+Opus offer -> POST -> answer) and attaches the media
// to a <video>. AV1-over-WebRTC decodes in Chrome/Edge; on other browsers (or any
// failure) the caller falls back to HLS.

import { servicePaths } from "./config";

// DEFAULT_ICE is the STUN-only fallback: the server advertises public NAT1To1 host
// candidates, so a directly-connectable viewer needs only STUN. It is used when the
// per-stream /ice endpoint (STUN + a short-lived coturn TURN credential) is
// unreachable; a restrictive-NAT viewer with only this will fail ICE and fall back to
// LL-HLS.
export const DEFAULT_ICE: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

// whepUrlFromPlayback derives the WHEP endpoint from a minted HLS playback URL
// (…/v1/live-streams/{id}/pb/{token}/hls/index.m3u8) by lifting the token and
// pointing it at the webrtc service. Returns null if the token cannot be found.
export function whepUrlFromPlayback(streamId: string, playbackUrl: string): string | null {
  const m = playbackUrl.match(/\/pb\/([^/]+)\//);
  if (!m) return null;
  const token = m[1];
  return `${servicePaths.webrtc}/v1/live-streams/${encodeURIComponent(streamId)}/pb/${token}/whep`;
}

// llHlsUrlFromWhep derives the LL-HLS MASTER playlist URL from a WHEP URL (same
// stream + token), so a client that cannot do WebRTC falls back to the ~2-4s
// part-based LL-HLS rather than the ~15s S3 HLS. The master is used (not the media
// playlist directly) so hls.js discovers the EXT-X-MEDIA audio rendition and plays
// sound; a video-only stream's master just points at the single video variant.
export function llHlsUrlFromWhep(whepUrl: string): string {
  return whepUrl.replace(/\/whep$/, "/llhls/master.m3u8");
}

// iceUrlFromWhep derives the ICE-config endpoint (same stream + token) from a WHEP URL.
export function iceUrlFromWhep(whepUrl: string): string {
  return whepUrl.replace(/\/whep$/, "/ice");
}

// fetchIceServers asks the server for the ICE list (STUN + a short-lived coturn TURN
// credential), so a viewer behind a restrictive NAT relays through TURN into WebRTC
// instead of failing ICE and falling back to HLS. Any failure (endpoint missing, bad
// JSON, network) yields the STUN-only DEFAULT_ICE, so it never blocks a connect.
export async function fetchIceServers(whepUrl: string): Promise<RTCIceServer[]> {
  try {
    const resp = await fetch(iceUrlFromWhep(whepUrl), { method: "GET" });
    if (!resp.ok) return DEFAULT_ICE;
    const body = (await resp.json()) as { iceServers?: RTCIceServer[] };
    if (Array.isArray(body?.iceServers) && body.iceServers.length > 0) return body.iceServers;
    return DEFAULT_ICE;
  } catch {
    return DEFAULT_ICE;
  }
}

export interface WhepSession {
  close: () => void;
}

export interface WhepOptions {
  iceServers?: RTCIceServer[];
  // mediaTimeoutMs bounds how long we wait for the first inbound track before
  // declaring failure (so a stuck ICE falls back to HLS quickly). Default 8000.
  mediaTimeoutMs?: number;
}

// connectWhep performs the WHEP handshake and attaches media to the video element.
// It resolves once a remote track is received; it rejects on any failure (non-201,
// ICE failure, or media timeout) so the caller can fall back. Always call close()
// (returned on success) to tear down the PeerConnection.
export function connectWhep(video: HTMLVideoElement, whepUrl: string, opts: WhepOptions = {}): Promise<WhepSession> {
  const timeoutMs = opts.mediaTimeoutMs ?? 8000;

  return new Promise<WhepSession>((resolve, reject) => {
    let settled = false;
    let pc: RTCPeerConnection | undefined;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };
    const fail = (err: Error) => done(() => {
      try { pc?.close(); } catch { /* ignore */ }
      reject(err);
    });

    const timer = window.setTimeout(() => fail(new Error("whep: no media within timeout")), timeoutMs);

    (async () => {
      // Resolve ICE first: unless the caller supplied servers, fetch STUN + a short-lived
      // coturn TURN credential from the server so a restrictive-NAT viewer can relay.
      const iceServers = opts.iceServers ?? await fetchIceServers(whepUrl);
      pc = new RTCPeerConnection({ iceServers });

      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });
      pc.ontrack = (e) => {
        if (e.track.kind !== "video") return;
        video.srcObject = e.streams[0];
        done(() => {
          window.clearTimeout(timer);
          resolve({ close: () => { try { pc?.close(); } catch { /* ignore */ } } });
        });
      };
      pc.oniceconnectionstatechange = () => {
        if (pc && pc.iceConnectionState === "failed") fail(new Error("whep: ICE failed"));
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      // Non-trickle: wait for ICE gathering to complete (bounded), then POST once.
      await new Promise<void>((r) => {
        if (pc!.iceGatheringState === "complete") return r();
        const onchg = () => { if (pc!.iceGatheringState === "complete") { pc!.removeEventListener("icegatheringstatechange", onchg); r(); } };
        pc!.addEventListener("icegatheringstatechange", onchg);
        setTimeout(r, 2000);
      });
      const resp = await fetch(whepUrl, {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: pc.localDescription?.sdp ?? "",
      });
      if (resp.status === 503) throw new Error("whep: stream at capacity");
      if (!resp.ok) throw new Error(`whep: ${resp.status}`);
      const answer = await resp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    })().catch((e) => fail(e instanceof Error ? e : new Error(String(e))));
  });
}
